import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-daisyui";
import { useTranslation } from "react-i18next";

import { IProjectDetailModalProps } from "./types";

import Button from "~/components/atoms/Button";
import { Icon } from "~/components/atoms/Icons";
import IconClose from "~/components/atoms/Icons/IconClose";
import Typo from "~/components/atoms/Typo";
import { cn } from "~/utilities/cn";
import { ETypoColor } from "~/utilities/enums/Colors";
import { EIcon } from "~/utilities/enums/Icons";
import { ETypoTag, ETypoVariant } from "~/utilities/enums/Typo";

const COLUMNS_EACH_ROW = 3;

const ProjectDetailModal: React.FC<IProjectDetailModalProps> = ({
  project,
  open,
  onClose,
  onShowFlat,
  onShowPriceList,
}) => {
  const { t } = useTranslation();
  // const navigate = useNavigate();

  const [unitTypeSelected, setUnitTypeSelected] = useState<string[]>([]);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose([]);
      }
    };
    if (open) {
      window.addEventListener("keydown", close);
    } else {
      window.removeEventListener("keydown", close);
    }
    return () => window.removeEventListener("keydown", close);
  }, [onClose, open]);

  const unitTypes = useMemo(() => {
    return (
      project?.unitTypes?.filter(
        (unitType) => !!unitType?.id && unitType.availableUnits >= 1
      ) || []
    );
  }, [project?.unitTypes]);

  const isOpen = open && unitTypes?.length > 0;

  const calculatorRow = useMemo(() => {
    const totalItems = unitTypes.length;
    const totalRow = Math.ceil(totalItems / COLUMNS_EACH_ROW);
    const startIndexLastRow = (totalRow - 1) * COLUMNS_EACH_ROW;
    const totalLastRow = totalItems - startIndexLastRow;

    return {
      totalLastRow,
      startIndexLastRow,
    };
  }, [unitTypes]);

  return (
    <Modal.Legacy
      open={isOpen}
      className={cn(
        "flex w-11/12 max-w-[864px] flex-col gap-3 overflow-y-hidden !bg-modalProjectDetailBackground !p-0 lg:max-h-[90dvh]"
      )}
      onClickBackdrop={() => onClose([])}
    >
      <Modal.Header className="sticky top-0 z-50 mb-0 flex items-center justify-center gap-3 bg-modalProjectDetailBackground p-5 pb-0 font-bold">
        <div className="flex w-full items-center justify-center gap-3  bg-modalProjectDetailBackground">
          <Typo
            variant={ETypoVariant.HEADER_32}
            tag="h4"
            className="mx-7 flex-1 truncate text-center font-bold lg:text-header-20"
            color={ETypoColor.MODAL_PROJECT_DETAIL_TEXT}
          >
            {project?.name}
          </Typo>
          <IconClose
            onClick={() => {
              onClose(unitTypeSelected);
            }}
          />
        </div>
      </Modal.Header>
      <Modal.Body className="scroll overflow-auto px-5">
        <div className="overflow-hidden">
          {open && (
            <>
              {(project?.unitTypes?.filter((unitType) => !!unitType?.id)
                ?.length || 0) === 0 && (
                <Typo
                  tag={ETypoTag.P}
                  variant={ETypoVariant.HEADER_20}
                  color={ETypoColor.MODAL_PROJECT_DETAIL_TEXT}
                  className="w-full  py-2 text-center font-bold capitalize"
                >
                  {t("no_unit_type_to_display")}
                </Typo>
              )}
              {(project?.unitTypes?.filter((unitType) => !!unitType?.id)
                ?.length || 0) > 0 && (
                <div
                  className={cn(
                    " flex flex-wrap justify-center gap-0  gap-y-6 divide-x divide-modalProjectDetailDivide [clip-path:inset(0_0_0_theme(divideWidth.2))] lg:flex-col lg:gap-0 "
                  )}
                >
                  {unitTypes?.map((unitType, index) => {
                    const isItemLastRow =
                      index >= calculatorRow.startIndexLastRow;

                    const isLeftItem =
                      calculatorRow.totalLastRow === 2 &&
                      calculatorRow.startIndexLastRow === index;

                    const isRightItem =
                      calculatorRow.totalLastRow === 2 &&
                      calculatorRow.startIndexLastRow + 1 === index;

                    const isOnlyItemLastRow =
                      calculatorRow.totalLastRow === 1 && isItemLastRow;
                    return (
                      <div
                        key={unitType?.id}
                        className={cn(
                          "px-[18px] py-3",
                          "flex w-1/3 flex-grow lg:w-full"
                        )}
                      >
                        <div
                          className={cn(
                            "flex w-full max-w-full flex-row items-center  justify-center gap-3 lg:w-full  lg:flex-col lg:gap-0",
                            isLeftItem && "!ml-auto w-fit",
                            isRightItem && "!mr-auto w-fit",
                            isOnlyItemLastRow &&
                              "mx-auto w-fit min-w-[calc(100%/3)] px-3"
                          )}
                        >
                          <Typo
                            tag={ETypoTag.P}
                            variant={ETypoVariant.HEADER_16}
                            color={ETypoColor.MODAL_PROJECT_DETAIL_TEXT}
                            className="flex-1  truncate  font-bold"
                          >
                            {unitType?.title || "N/A"}
                          </Typo>
                          <Typo
                            tag={ETypoTag.P}
                            variant={ETypoVariant.HEADER_16}
                            color={
                              unitType?.availableUnits < 1
                                ? ETypoColor.MODAL_PROJECT_DETAIL_SOLD_OUT
                                : ETypoColor.MODAL_PROJECT_DETAIL_AVAILABLE
                            }
                            className="w-fit flex-shrink-0 font-bold capitalize"
                          >
                            {unitType?.availableUnits < 1
                              ? t("sold_out")
                              : t("available")}
                          </Typo>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions className="sticky bottom-0 mt-0 flex items-center justify-center gap-4 bg-modalProjectDetailBackground p-5 pt-0 lg:flex-col">
        <Button
          variant="tab"
          type="button"
          className={cn(
            "!rounded-[21.5px] !border !border-solid px-[18px] py-3 text-sub-title-16 font-bold lg:text-sub-title-14",
            "border-modalProjectDetailShowFlatAppointmentBorder !bg-modalProjectDetailShowFlatAppointmentBackground"
          )}
          onClick={onShowFlat}
        >
          <Icon TypeIcon={EIcon.WhiteCalendarIcon} height={18} width={18} />
          <Typo
            tag={ETypoTag.P}
            variant={ETypoVariant.HEADER_16}
            color={ETypoColor.MODAL_PROJECT_DETAIL_SHOW_FLAT_APPOINTMENT}
            className="font-bold capitalize"
          >
            {t("showflat_appointment_brochure")}
          </Typo>
        </Button>
        <Button
          variant="tab"
          type="button"
          className={cn(
            "!rounded-[21.5px] !border !border-solid px-[18px] py-3 text-sub-title-16 font-bold lg:text-sub-title-14",
            "border-modalProjectDetailSoldOutButtonBorder !bg-modalProjectDetailSoldOutButtonBackground"
          )}
          onClick={() => {
            onShowPriceList();
          }}
        >
          <Icon TypeIcon={EIcon.MoneyIcon} height={18} width={18} />
          <Typo
            tag={ETypoTag.P}
            variant={ETypoVariant.HEADER_16}
            color={ETypoColor.MODAL_PROJECT_DETAIL_SOLD_OUT_BUTTON}
            className="font-bold capitalize"
          >
            {t("priceList")}
          </Typo>
        </Button>
      </Modal.Actions>
    </Modal.Legacy>
  );
};

export default ProjectDetailModal;
